import { addDays, format } from 'date-fns';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Reservation } from '@/api/reservations/types';
import { useCancelReservation } from '@/api/reservations/use-cancel-reservation';
import { usePreviewCancelReservation } from '@/api/reservations/use-preview-cancel-reservation';
import { useReservation } from '@/api/reservations/use-reservation';
import BackButton from '@/components/back-button';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { RefundMethodOption } from '@/components/trip-details/refund-method-option';
import {
  capitalizeFirstLetter,
  formatNumber,
  getCancellationScenario,
  getCancelState,
  isDesktop,
} from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { GREY_40_50 } from '@/style/colors';
import { Button, Image, showErrorMessage, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { Checkbox } from '@/ui/checkbox';
import { ChipType } from '@/ui/chip';
import Collapsible from '@/ui/collapsible';
import { IconBadge } from '@/ui/icon-badge';
import { CreditCard } from '@/ui/icons/credit-card';
import { InputMulti } from '@/ui/input-multi';
import { Loader } from '@/ui/loader';

const REFUND_PROGRESS_STEPS = ['Requested', 'Processing', 'Issued'];

const CANCELLATION_OPTIONS = [
  'Plans changed',
  'Found a better price',
  'Wrong dates',
  'Problem with the car',
  'Booked by mistake',
  'Location change',
  'Other',
];

type Step = 'reason' | 'refund';

const eonCreditDarkSrc = require('../../../../../public/images/eon-credit-dark.png');
const eonCreditLightSrc = require('../../../../../public/images/eon-credit-light.png');
const refundCheckmarkDarkSrc = require('../../../../../public/images/refund-checkmark-dark.png');
const refundCheckmarkLightSrc = require('../../../../../public/images/refund-checkmark-light.png');
const eonCreditMarkSrc = require('../../../../../public/images/eon-credit-mark.png');

type RefundLineItem = {
  key: string;
  label: string;
  sublabel?: string;
  amount: number;
  isDeduction?: boolean;
};

const buildRefundLineItems = ({
  resData,
  startDay = 1,
  endDay,
  rentalLabel,
  insuranceLabel,
  includeAllOrNothingCosts = true,
}: {
  resData: Reservation;
  startDay?: number;
  endDay: number;
  rentalLabel: string;
  insuranceLabel: string;
  // Extras + delivery are never prorated: the backend either refunds all of
  // them or none, so they only belong in the full-refund breakdown and the
  // partial-refund "Non-refundable" section -- not the partial-refund
  // "Back as Eon credit" portion.
  includeAllOrNothingCosts?: boolean;
}): RefundLineItem[] => {
  const items: RefundLineItem[] = [];
  let rateStartDay = 1;

  resData.reservationRates.forEach((rate) => {
    const rateEndDay = rateStartDay + rate.numDays - 1;
    const overlapDays =
      Math.min(rateEndDay, endDay) - Math.max(rateStartDay, startDay) + 1;
    rateStartDay = rateEndDay + 1;

    if (overlapDays <= 0) return;

    const dayLabel = overlapDays === 1 ? '1 day' : `${overlapDays} days`;
    items.push({
      key: `rental-${rate.id}`,
      label: rentalLabel,
      sublabel: `${dayLabel} x $${formatNumber(rate.dailyRate)}`,
      amount: overlapDays * rate.dailyRate,
    });

    if (rate.discountAmount) {
      items.push({
        key: `discount-${rate.id}`,
        label: rate.discountType,
        sublabel: 'Prorated for unused days',
        amount: (rate.discountAmount / rate.numDays) * overlapDays,
        isDeduction: true,
      });
    }
  });

  const totalDays = endDay - startDay + 1;
  if (resData.insuranceCost > 0) {
    const insuranceDayLabel = totalDays === 1 ? '1 day' : `${totalDays} days`;
    items.push({
      key: 'insurance',
      label: insuranceLabel,
      sublabel: `${insuranceDayLabel} x $${formatNumber(
        resData.insuranceCost / resData.numDays
      )}`,
      amount: (resData.insuranceCost / resData.numDays) * totalDays,
    });
  }

  resData.extras.forEach((extra) => {
    if (extra.tag === 'custom_delivery') return;
    if (!includeAllOrNothingCosts) return;

    if (extra.recurring) {
      // Extras are refunded all-or-nothing (never prorated day-to-day), so
      // this always uses the full trip length, not this section's totalDays.
      const extraDayLabel =
        resData.numDays === 1 ? '1 day' : `${resData.numDays} days`;
      items.push({
        key: `extra-${extra.id}`,
        label: extra.title,
        sublabel: `${extraDayLabel} x $${formatNumber(extra.amount)}`,
        amount: resData.numDays * extra.amount,
      });
    } else {
      items.push({
        key: `extra-${extra.id}`,
        label: extra.title,
        amount: extra.amount,
      });
    }
  });

  if (resData.totalDeliveryCost > 0 && includeAllOrNothingCosts) {
    items.push({
      key: 'delivery',
      label: 'Delivery fee',
      amount: resData.totalDeliveryCost,
    });
  }

  return items;
};

export default function CancelTrip() {
  const { styles, theme } = useStyles(stylesheet);
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const [step, setStep] = useState<Step>('reason');
  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    undefined
  );
  const [reasonText, setReasonText] = useState('');
  const [refundDestination, setRefundDestination] = useState<
    'credits' | 'card'
  >('credits');
  const [consentChecked, setConsentChecked] = useState(false);
  const scrollViewRef = useRef<KeyboardAwareScrollView>(null);

  const { data: resData, isPending: isFetchingReservation } = useReservation({
    variables: { id: Number(id) },
    enabled: !!id,
  });

  const {
    mutateAsync: cancelReservation,
    isPending: isCancellingReservation,
    isSuccess: hasCancelledReservation,
  } = useCancelReservation();

  const {
    mutate: fetchRefundPreview,
    data: refundPreview,
    isPending: isPendingRefundPreview,
  } = usePreviewCancelReservation();

  const cancelState = useMemo(
    () => (resData ? getCancelState(resData) : null),
    [resData]
  );

  const isRefundMethodSelectionStep =
    cancelState === 'non-refundable-grace' ||
    cancelState === 'fully-refundable';

  const cancellationScenario = useMemo(
    () => (cancelState ? getCancellationScenario(cancelState) : null),
    [cancelState]
  );

  const isReasonStep = step === 'reason';
  const headerTitle = isReasonStep ? 'Reason For Cancellation' : 'Refund';

  useEffect(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current?.scrollToPosition(0, 0, false);
    }
  }, [step]);

  useEffect(() => {
    if (
      step === 'refund' &&
      cancelState &&
      cancelState !== 'non-refundable' &&
      !refundPreview
    ) {
      fetchRefundPreview({ id: Number(id) });
    }
  }, [step, cancelState, refundPreview, fetchRefundPreview, id]);

  const currentPageName = useMemo(() => {
    if (
      hasCancelledReservation ||
      (cancelState === 'non-refundable' && step === 'refund')
    ) {
      return 'Cancellation Confirmation';
    }

    if (step === 'reason') return 'Cancellation Reason';
    if (step === 'refund') return 'Cancellation Refund Method';

    return null;
  }, [step, cancelState, hasCancelledReservation]);

  useEffect(() => {
    if (!currentPageName || !cancellationScenario) return;

    mixpanelService.track('Page Viewed', {
      'Page Name': currentPageName,
      'Cancellation Scenario': cancellationScenario,
    });
  }, [currentPageName, cancellationScenario]);

  const handleCancelTrip = async () => {
    try {
      await cancelReservation({
        id: Number(id),
        cancellationReason: selectedOption,
        cancellationReasonDetails: reasonText,
        refundDestination,
      });
      Toast.show({ type: 'success', text1: 'Your trip has been cancelled' });

      if (cancelState === 'non-refundable') {
        setStep('refund');
      }
    } catch (err) {
      showErrorMessage(
        err instanceof Error ? err.message : 'Error cancelling trip'
      );
    }
  };

  const handleBack = () => {
    if (hasCancelledReservation) {
      router.navigate(`/trips`);
      return;
    }

    if (step === 'refund') {
      setStep('reason');
      return;
    }

    if (router.canGoBack()) {
      router.back();
    } else {
      router.navigate(`/trips/${id}/details`);
    }
  };

  const trackReasonSelected = (wasSkipped: boolean) => {
    mixpanelService.track('Cancellation Reason Selected', {
      Reason: wasSkipped ? null : selectedOption ?? null,
      'Detail Text': !wasSkipped && reasonText.trim() ? reasonText : null,
      'Was Skipped': wasSkipped,
      'Cancellation Scenario': cancellationScenario,
    });
  };

  const handlePrimaryPress = async () => {
    if (isReasonStep) {
      trackReasonSelected(false);

      if (cancelState === 'non-refundable') {
        handleCancelTrip();
        return;
      }

      setStep('refund');
      return;
    }

    if (hasCancelledReservation) {
      router.navigate('/trips');
      return;
    }

    if (
      ['non-refundable-grace', 'fully-refundable', 'partial-refund'].includes(
        cancelState!
      )
    ) {
      mixpanelService.track('Refund Method Selected', {
        Method: refundDestination === 'credits' ? 'Eon Credit' : 'Card',
        'Refund Amount': Math.round(
          (refundPreview?.data.refundAmount ?? 0) * 100
        ),
        'Cancellation Scenario': cancellationScenario,
      });
      handleCancelTrip();
      return;
    }

    router.navigate(`/trips/${id}/details`);
  };

  const handleSkip = () => {
    trackReasonSelected(true);
    setSelectedOption(undefined);
    setReasonText('');
    setStep('refund');
  };

  const eonCreditIcon = (
    <Image
      source={eonCreditMarkSrc}
      style={styles.creditIconBadge}
      contentFit="cover"
    />
  );

  const imageElement = useMemo(() => {
    let imgSrc;
    let width;
    let aspectRatio = 1;
    if (refundDestination === 'credits' && cancelState !== 'non-refundable') {
      imgSrc =
        UnistylesRuntime.themeName === 'dark'
          ? eonCreditDarkSrc
          : eonCreditLightSrc;
      width = 125;
      aspectRatio = 373 / 240;
    } else {
      imgSrc =
        UnistylesRuntime.themeName === 'dark'
          ? refundCheckmarkDarkSrc
          : refundCheckmarkLightSrc;
      width = 80;
      aspectRatio = 1;
    }

    return (
      <Image
        source={imgSrc}
        style={{ width: width, aspectRatio: aspectRatio, alignSelf: 'center' }}
        contentFit="contain"
      />
    );
  }, [refundDestination, cancelState]);

  const renderLineItem = (item: RefundLineItem) => (
    <View key={item.key} style={styles.lineItemRow}>
      <View>
        <Text type="bodyRegular">{item.label}</Text>

        {item.sublabel ? (
          <Text type="caption1" style={styles.textSecondary}>
            {item.sublabel}
          </Text>
        ) : null}
      </View>

      <Text
        type="bodyRegular"
        style={item.isDeduction ? styles.textError : undefined}
      >
        {item.isDeduction ? '-' : ''}${formatNumber(item.amount)}
      </Text>
    </View>
  );

  const renderReasonStep = () => (
    <View style={{ flexGrow: 1, gap: 24 }}>
      <View style={{ gap: 8 }}>
        <Text type="title2">Help Us Improve</Text>

        <Text type="bodyRegular" style={{ color: theme.colors.text.secondary }}>
          This won't affect your refund
        </Text>
      </View>

      <View style={{ gap: 16, flexDirection: 'row', flexWrap: 'wrap' }}>
        {CANCELLATION_OPTIONS.map((option) => (
          <Button
            key={option}
            type={selectedOption === option ? 'primarySmall' : 'secondarySmall'}
            text={option}
            onPress={() => setSelectedOption(option)}
            style={{ width: 'auto' }}
          />
        ))}
      </View>

      <InputMulti
        label="Tell us more"
        text={reasonText}
        onChangeText={(s: string) => setReasonText(s)}
        placeholder="Add more detail (optional)"
        style={{ minHeight: 125 }}
      />
    </View>
  );

  const renderRefundStep = () => {
    if (!cancelState || !resData) return null;

    if (cancelState === 'non-refundable') {
      return (
        <View
          style={{
            flexGrow: 1,
            gap: 24,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {imageElement}

          <Text type="title1">Your Trip Has Been Cancelled</Text>
        </View>
      );
    }

    if (!refundPreview) return null;

    const hasCreditPortion = refundPreview.data.creditPaid > 0;
    const isSplitRefund = refundDestination === 'card' && hasCreditPortion;

    if (hasCancelledReservation) {
      const showCreditRow = refundDestination === 'credits' || isSplitRefund;
      const showCardRow = refundDestination === 'card';
      const isInstantRefund = refundDestination === 'credits';
      const creditAmount = isSplitRefund
        ? Math.min(
            refundPreview.data.refundAmount,
            refundPreview.data.creditPaid
          )
        : refundPreview.data.refundAmount;
      const cardAmount = isSplitRefund
        ? Math.min(
            refundPreview.data.refundAmount - creditAmount,
            refundPreview.data.cardPaid
          )
        : refundPreview.data.refundAmount;
      const cardArrivalDate = format(addDays(new Date(), 7), 'MMM d');

      return (
        <View style={styles.stepGap24}>
          <View style={styles.gap8}>
            <Text type="bodyRegular" style={styles.textSecondary}>
              Total Refund
            </Text>

            <Text type="largeTitle">
              ${formatNumber(refundPreview.data.refundAmount)}
            </Text>
          </View>

          <View style={styles.progressStepsRow}>
            {REFUND_PROGRESS_STEPS.map((label, index) => (
              <View key={label} style={styles.progressStepColumn}>
                <View
                  style={[
                    styles.progressStepBar,
                    {
                      backgroundColor:
                        index < (isInstantRefund ? 3 : 2)
                          ? theme.colors.text.primary
                          : GREY_40_50,
                    },
                  ]}
                />

                <Text
                  type="caption2"
                  style={{
                    textAlign:
                      index === 1 ? 'center' : index === 2 ? 'right' : 'left',
                  }}
                >
                  {label}
                </Text>
              </View>
            ))}
          </View>

          <View style={styles.gap24}>
            <Text type="title2">Summary</Text>

            <View style={styles.gap16}>
              <View style={styles.summaryCard}>
                {showCreditRow ? (
                  <View style={styles.rowCenterGap8}>
                    {eonCreditIcon}

                    <View style={styles.rowTextGroup}>
                      <Text type="bodyRegular">Eon credit</Text>

                      <Text type="caption2" style={styles.textSecondary}>
                        Added to your balance
                      </Text>
                    </View>

                    <Text type="bodyBold" style={styles.textSuccess}>
                      ${formatNumber(creditAmount)}
                    </Text>
                  </View>
                ) : null}

                {showCardRow ? (
                  <View style={styles.rowCenterGap8}>
                    <IconBadge icon={<CreditCard />} size={28} iconSize={16} />

                    <View style={styles.rowTextGroup}>
                      <Text type="bodyRegular">
                        {capitalizeFirstLetter(resData.originalCard.brand)}{' '}
                        ending {resData.originalCard.last4}
                      </Text>

                      <Text type="caption2" style={styles.textSecondary}>
                        Arrives by {cardArrivalDate}
                      </Text>
                    </View>

                    <Text type="bodyBold" style={styles.textSecondary}>
                      ${formatNumber(cardAmount)}
                    </Text>
                  </View>
                ) : null}
              </View>

              {showCardRow ? (
                <Alert
                  type={AlertType.BRAND}
                  title="Disclaimer"
                  body="Credit Card processing usually takes 3-7 days. We will email you a confirmation."
                />
              ) : null}
            </View>
          </View>
        </View>
      );
    }

    if (isRefundMethodSelectionStep) {
      const creditAmount = hasCreditPortion
        ? Math.min(
            refundPreview.data.refundAmount,
            refundPreview.data.creditPaid
          )
        : refundPreview.data.refundAmount;
      const cardAmount = hasCreditPortion
        ? Math.min(
            refundPreview.data.refundAmount - creditAmount,
            refundPreview.data.cardPaid
          )
        : refundPreview.data.refundAmount;
      const hasCardOption = cardAmount > 0;

      const eonCreditOption = (
        <RefundMethodOption
          title="Eon Credit"
          chipText="Instant"
          chipType={ChipType.PENDING}
          active={refundDestination === 'credits'}
          onPress={
            hasCardOption ? () => setRefundDestination('credits') : undefined
          }
          style={hasCardOption ? styles.fullHeight : undefined}
          rows={[
            {
              icon: eonCreditIcon,
              label: 'Eon credit',
              sublabel: 'Use it on your next trip',
              amount: `$${formatNumber(refundPreview.data.refundAmount)}`,
            },
          ]}
        />
      );

      return (
        <View style={styles.stepGap16}>
          <View style={styles.gap8}>
            <Text type="title2">Select a Refund Method</Text>

            <Text type="bodyRegular" style={styles.textSecondary}>
              Choose Eon credit or your original card
            </Text>
          </View>

          {hasCardOption ? (
            <View style={styles.methodOptionsRow}>
              <View style={styles.flex1}>{eonCreditOption}</View>

              <View style={styles.flex1}>
                <RefundMethodOption
                  title="Original Method"
                  chipText="3-7 days"
                  chipType={ChipType.WARNING}
                  active={refundDestination === 'card'}
                  onPress={() => setRefundDestination('card')}
                  style={styles.fullHeight}
                  rows={[
                    ...(hasCreditPortion
                      ? [
                          {
                            icon: eonCreditIcon,
                            label: 'Eon credit',
                            sublabel: 'Use it on your next trip',
                            amount: `$${formatNumber(creditAmount)}`,
                          },
                        ]
                      : []),
                    {
                      icon: <IconBadge icon={<CreditCard />} />,
                      label: 'Credit card',
                      sublabel: `${capitalizeFirstLetter(
                        resData.originalCard.brand
                      )} ending in ${resData.originalCard.last4}`,
                      amount: `$${formatNumber(cardAmount)}`,
                    },
                  ]}
                />
              </View>
            </View>
          ) : (
            <View>{eonCreditOption}</View>
          )}

          <View style={styles.gap8}>
            <Text type="title2">Refund Breakdown</Text>

            <Collapsible
              title="Total Refund"
              headerRight={
                <Text type="title2" style={styles.textSuccess}>
                  ${formatNumber(refundPreview.data.refundAmount)}
                </Text>
              }
            >
              <View style={styles.collapsibleBody}>
                {buildRefundLineItems({
                  resData,
                  endDay: resData.numDays,
                  rentalLabel: 'Car Rental',
                  insuranceLabel: 'Insurance',
                }).map(renderLineItem)}
              </View>
            </Collapsible>
          </View>
        </View>
      );
    }

    if (cancelState === 'partial-refund') {
      const notRefundedAmount =
        resData.totalCost - refundPreview.data.refundAmount;

      return (
        <View style={styles.stepGap16}>
          <View style={styles.gap8}>
            <Text type="title2">
              You'll Get ${formatNumber(refundPreview.data.refundAmount)} As Eon
              Credit
            </Text>

            <Text type="bodyRegular" style={styles.textSecondary}>
              Your original charge is ${formatNumber(resData.totalCost)}. One
              day of rental will not be refunded.
            </Text>
          </View>

          <RefundMethodOption
            title="Eon Credit"
            chipText="Instant"
            chipType={ChipType.PENDING}
            active
            rows={[
              {
                icon: eonCreditIcon,
                label: 'Eon credit',
                sublabel: 'Use it on your next trip',
                amount: `$${formatNumber(refundPreview.data.refundAmount)}`,
              },
            ]}
          />

          <View style={styles.gap8}>
            <Text type="title2">Refund Breakdown</Text>

            <View>
              <Collapsible
                title="Back as Eon credit"
                groupPosition="top"
                headerRight={
                  <Text type="title2" style={styles.textSuccess}>
                    ${formatNumber(refundPreview.data.refundAmount)}
                  </Text>
                }
              >
                <View style={styles.collapsibleBody}>
                  {buildRefundLineItems({
                    resData,
                    startDay: 2,
                    endDay: resData.numDays,
                    rentalLabel: 'Unused rental',
                    insuranceLabel: 'Unused insurance',
                    includeAllOrNothingCosts: false,
                  }).map(renderLineItem)}
                </View>
              </Collapsible>

              <Collapsible
                title="Non-refundable"
                groupPosition="bottom"
                headerRight={
                  <Text type="title2" style={styles.textSecondary}>
                    ${formatNumber(notRefundedAmount)}
                  </Text>
                }
              >
                <View style={styles.collapsibleBody}>
                  {buildRefundLineItems({
                    resData,
                    endDay: 1,
                    rentalLabel: 'Day 1 used rental',
                    insuranceLabel: `Day 1 ${resData.insurance.category}`,
                  }).map(renderLineItem)}

                  {resData.totalCancellationPolicyCost > 0
                    ? renderLineItem({
                        key: 'trip-fee',
                        label: 'Refundable trip fee',
                        amount: resData.totalCancellationPolicyCost,
                      })
                    : null}

                  <Text type="caption1" style={styles.textSecondary}>
                    One day of rental is deducted to cover operational costs.
                  </Text>
                </View>
              </Collapsible>
            </View>
          </View>

          <View style={styles.consentRow}>
            <Checkbox
              active={consentChecked}
              onPress={() => setConsentChecked((prev) => !prev)}
            />

            <Text type="caption1" style={styles.consentText}>
              I understand my refund will come back as Eon credit, minus one day
              of rental.
            </Text>
          </View>
        </View>
      );
    }

    return <View />;
  };

  const buttonText = useMemo(() => {
    if (isReasonStep) {
      return 'Cancel My Trip';
    }

    if (hasCancelledReservation) {
      return 'Back To Trips';
    }

    if (
      ['non-refundable-grace', 'fully-refundable', 'partial-refund'].includes(
        cancelState!
      )
    ) {
      return 'Confirm Refund';
    }

    return 'Continue';
  }, [cancelState, isReasonStep, hasCancelledReservation]);

  const buttonDisabled = useMemo(() => {
    if (isReasonStep) {
      if (cancelState === 'non-refundable') {
        return isCancellingReservation;
      }

      return !selectedOption || isCancellingReservation;
    }

    if (hasCancelledReservation) {
      return false;
    }

    if (cancelState === 'partial-refund') {
      return (
        !consentChecked || isPendingRefundPreview || isCancellingReservation
      );
    }

    if (isRefundMethodSelectionStep) {
      return (
        !refundDestination || isPendingRefundPreview || isCancellingReservation
      );
    }

    return !selectedOption || isCancellingReservation;
  }, [
    cancelState,
    isRefundMethodSelectionStep,
    selectedOption,
    isCancellingReservation,
    isPendingRefundPreview,
    refundDestination,
    consentChecked,
    isReasonStep,
    hasCancelledReservation,
  ]);

  const isLoadingRefundPreview =
    step === 'refund' &&
    cancelState &&
    cancelState !== 'non-refundable' &&
    !refundPreview;

  const isNonRefundableConfirmation =
    !isReasonStep && cancelState === 'non-refundable';

  if (
    isFetchingReservation ||
    isCancellingReservation ||
    isPendingRefundPreview ||
    isLoadingRefundPreview
  ) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 200, height: 200 }} />
      </View>
    );
  }

  return (
    <KeyboardAwareScrollView
      ref={scrollViewRef}
      style={styles.scrollViewContainer}
      contentContainerStyle={styles.scrollViewContentContainer}
      keyboardShouldPersistTaps="always"
      showsVerticalScrollIndicator={false}
      enableOnAndroid
      enableResetScrollToCoords={false}
      extraScrollHeight={0}
    >
      {!isDesktop() ? (
        <Stack.Screen
          options={{
            title: headerTitle,
            headerTitle,
            headerLeft: hasCancelledReservation
              ? () => null
              : () => <BackButton onPress={handleBack} />,
            headerBackVisible: false,
          }}
        />
      ) : null}

      <View style={styles.container}>
        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) &&
        !hasCancelledReservation ? (
          <View>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={handleBack}
            />
          </View>
        ) : null}

        <View
          style={[
            styles.stepContainer,
            isNonRefundableConfirmation && isDesktop()
              ? styles.stepContainerCentered
              : null,
          ]}
        >
          <View style={{ flexGrow: !isDesktop() ? 1 : 0 }}>
            {isReasonStep ? renderReasonStep() : renderRefundStep()}
          </View>

          <View
            style={[
              styles.buttonsContainer,
              isNonRefundableConfirmation && isDesktop()
                ? styles.buttonsContainerCentered
                : null,
            ]}
          >
            <Button
              type={
                hasCancelledReservation && cancelState !== 'non-refundable'
                  ? 'secondary'
                  : 'primary'
              }
              text={buttonText}
              onPress={handlePrimaryPress}
              loading={isCancellingReservation}
              disabled={buttonDisabled}
              style={
                isNonRefundableConfirmation
                  ? [styles.button, styles.nonRefundableButton]
                  : styles.button
              }
            />

            {isReasonStep && cancelState !== 'non-refundable' ? (
              <Button
                type="ghost"
                text="Skip"
                onPress={handleSkip}
                disabled={isCancellingReservation}
                style={styles.button}
              />
            ) : null}
          </View>
        </View>
      </View>
    </KeyboardAwareScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  scrollViewContainer: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  scrollViewContentContainer: {
    flexGrow: 1,
    padding: 16,
    alignItems: {
      md: 'center',
    },
  },
  container: {
    flexGrow: 1,
    gap: 16,
    width: {
      md: 600,
    },
    height: {
      md: 600,
    },
  },
  stepContainer: {
    flex: 1,
    justifyContent: {
      xs: 'space-between',
      md: 'flex-start',
    },
    gap: 24,
  },
  stepContainerCentered: {
    justifyContent: 'center',
  },
  buttonsContainerCentered: {
    justifyContent: 'center',
  },
  buttonsContainer: {
    flexDirection: {
      xs: 'column',
      md: 'row-reverse',
    },
    justifyContent: {
      xs: 'center',
      md: 'flex-start',
    },
    gap: 8,
    padding: 16,
    paddingBottom: 24,
  },
  button: {
    width: {
      xs: '100%',
      md: 175,
    },
  },
  summaryCard: {
    width: '100%',
    gap: 8,
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    backgroundColor: theme.colors.card.hover.backgroundColor,
    borderColor: theme.colors.card.default.borderColor,
  },
  lineItemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rowCenterGap8: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  flex1: {
    flex: {
      md: 1,
    },
  },
  fullHeight: {
    height: {
      md: '100%',
    },
  },
  gap8: {
    gap: 8,
  },
  gap16: {
    gap: 16,
  },
  gap24: {
    gap: 24,
  },
  stepGap16: {
    flexGrow: 1,
    gap: 16,
  },
  stepGap24: {
    flexGrow: 1,
    gap: 24,
  },
  rowTextGroup: {
    flex: 1,
    gap: 4,
  },
  textSecondary: {
    color: theme.colors.text.secondary,
  },
  textSuccess: {
    color: theme.colors.success,
  },
  textError: {
    color: theme.colors.error,
  },
  progressStepsRow: {
    flexDirection: 'row',
    gap: 9,
  },
  progressStepColumn: {
    flex: 1,
    gap: 5,
  },
  progressStepBar: {
    height: 4,
    width: '100%',
  },
  creditIconBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
  },
  methodOptionsRow: {
    gap: 16,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
  },
  collapsibleBody: {
    padding: 16,
    gap: 16,
  },
  consentRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-start',
  },
  consentText: {
    color: theme.colors.text.secondary,
    flex: 1,
  },
  nonRefundableButton: {
    width: {
      xs: '100%',
      md: 'auto',
    },
    minWidth: {
      md: 150,
    },
  },
}));
